/**
 * AppModals — gerencia a fila de modais de notificação.
 *
 * Regras:
 *  - Exibe um modal por vez, na ordem definida em MODALS.
 *  - Se o usuário já clicou no botão primário (localStorage), pula para o próximo.
 *  - Se o usuário fechar (X) ou clicar em "Agora não", descarta só nesta sessão
 *    e avança para o próximo modal.
 *  - Se `permission` estiver definida no modal, exibe apenas para usuários que
 *    possuem aquela permission.
 *
 * Para adicionar um novo modal, basta incluir uma entrada no array MODALS abaixo.
 */

import React, { useState } from "react";
import NotificationModal, { STORAGE_PREFIX } from "../NotificationModal";
import { useAccess } from "../../contexts/PermissionContext";

// ─── URLs (substituir pelas reais quando disponíveis) ──────────────────────────
const NPS_SURVEY_URL = "https://tupi-survey-cpos.tupinrg.app/survey/parceiros-2026";
const FULL_SERVICE_URL = "https://tupi-surveys.tupinrg.app/survey/simplifica";

// ─── Definição dos modais ──────────────────────────────────────────────────────
const MODALS = [
  {
    id: "nps-survey-2026-v2",
    permission: "show-nps-modal",
    title: "Como está sendo sua experiência com a Tupi?",
    body: "6 perguntas rápidas sobre a sua operação. Suas respostas definem o que a gente melhora primeiro.",
    primaryButtonLabel: "Responder pesquisa",
    onPrimary: () =>
      window.open(NPS_SURVEY_URL, "_blank", "noopener,noreferrer"),
    secondaryButtonLabel: "Agora não",
  },
  {
    id: "full-service-offer-2026-v1",
    permission: "show-full-service-modal",
    title: "Reforma Tributária chegando. Sua operação está pronta?",
    body: "A partir de 2027, a emissão de NFs em recargas será obrigatória. No Tupi Simplifica, a Tupi assume tudo isso por você.",
    primaryButtonLabel: "Quero receber uma proposta",
    onPrimary: () =>
      window.open(FULL_SERVICE_URL, "_blank", "noopener,noreferrer"),
    secondaryButtonLabel: "Agora não",
  },
];

// ─── sessionStorage helpers ────────────────────────────────────────────────────
const SESSION_KEY = "tupi_modal_session_dismissed";

const getSessionDismissed = () => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SESSION_KEY) || "[]"));
  } catch {
    return new Set();
  }
};

// ─── Componente ────────────────────────────────────────────────────────────────
export default function AppModals() {
  const { hasPermission, isLoaded } = useAccess();

  // Fix 3: state real inicializado a partir do sessionStorage — sobrevive navegação
  const [dismissedIds, setDismissedIds] = useState(() => getSessionDismissed());
  // Fix 1: ID do modal cuja animação de saída ainda está em curso
  const [closingId, setClosingId] = useState(null);
  const [isPermanent, setIsPermanent] = useState(false);

  // Aguarda o auth resolver antes de avaliar permissões
  if (!isLoaded) return null;

  const current = MODALS.find((m) => {
    if (dismissedIds.has(m.id)) return false;
    if (localStorage.getItem(STORAGE_PREFIX + m.id) === "1") return false;
    if (m.permission && !hasPermission(m.permission)) return false;
    return true;
  });

  // Enquanto a animação de saída está rodando, mantém o modal anterior montado
  const toShow = closingId ? MODALS.find((m) => m.id === closingId) : current;

  const dismiss = (id, permanent = false) => {
    setIsPermanent(permanent);
    setClosingId(id);
  };

  // Chamado pelo Modal do Ant Design após a animação de saída terminar
  const handleAfterClose = () => {
    if (isPermanent) localStorage.setItem(STORAGE_PREFIX + closingId, "1");
    setDismissedIds((prev) => {
      const next = new Set([...prev, closingId]);
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify([...next]));
      } catch {}
      return next;
    });
    setClosingId(null);
    setIsPermanent(false);
  };

  if (!toShow) return null;

  return (
    <NotificationModal
      key={toShow.id}
      visible={!closingId}
      afterClose={handleAfterClose}
      storageKey={toShow.id}
      title={toShow.title}
      body={toShow.body}
      imageUrl={toShow.imageUrl}
      primaryButtonLabel={toShow.primaryButtonLabel}
      onPrimary={() => { toShow.onPrimary(); dismiss(toShow.id, true); }}
      secondaryButtonLabel={toShow.secondaryButtonLabel}
      onSecondary={() => dismiss(toShow.id)}
      dismissable
      onDismiss={() => dismiss(toShow.id)}
    />
  );
}
